import { Alert, AlertTitle, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useFarmSubdivisions } from '../../hooks/data/useFarmSubdivisions'
import { useMetricEvents } from '../../hooks/data/useMetricEvents'
import { usePaddocksByIds } from '../../hooks/data/usePaddocks'
import { PROGRAM_IDS } from '../../utils/constants'
import { DCNamespaces } from '../../utils/dataCollection/namespaces'
import {
  findNonConsecutiveGrazingPlans,
  NonConsecutiveGrazingPlanPair,
} from '../../utils/dataCollection/nonConsecutiveGrazingPlans'

type NonConsecutiveGrazingPlansWarningProps = {
  farm: { id: string; programId?: number }
  year: string
}

const toDisplayDate = (calendarDate: string): string => calendarDate.split('-').reverse().join('/')

const describePair = (
  pair: NonConsecutiveGrazingPlanPair,
  paddockNameById: Map<string, string>,
): string => {
  const paddockNames = pair.paddockIds.map(paddockId => paddockNameById.get(paddockId) ?? paddockId)
  const paddocksLabel = paddockNames.length === 1 ? 'Lote' : 'Lotes'

  return `${paddocksLabel} ${paddockNames.join(', ')}: el plan cerrado termina el ${toDisplayDate(pair.closedPlanEndDate)} y el plan abierto comienza el ${toDisplayDate(pair.openPlanStartDate)}. El plan abierto debe comenzar el día siguiente.`
}

// Reads the same cache entries FormContainer uses for the grazing tab, so it adds no request and
// refreshes when a save or delete invalidates them.
const NonConsecutiveGrazingPlansWarning = ({
  farm,
  year,
}: NonConsecutiveGrazingPlansWarningProps) => {
  const isSaraFarm = farm.programId === PROGRAM_IDS.SARA

  const { data: grazingPlans } = useMetricEvents({
    farmId: farm.id,
    namespace: DCNamespaces.grazing,
    year,
    enabled: isSaraFarm && !!year,
  })
  const { data: farmSubdivisions } = useFarmSubdivisions({
    farmId: farm.id,
    year,
    enabled: isSaraFarm && !!year,
  })
  const { data: paddocks, isLoading: isLoadingPaddocks } = usePaddocksByIds({
    ids: farmSubdivisions?.[0]?.paddockIds,
  })

  const pairs = useMemo(() => findNonConsecutiveGrazingPlans(grazingPlans), [grazingPlans])
  const paddockNameById = useMemo(
    () => new Map<string, string>((paddocks ?? []).map(paddock => [paddock.id, paddock.name])),
    [paddocks],
  )

  if (!isSaraFarm || isLoadingPaddocks || !pairs.length) return null

  return (
    <Alert severity="warning" sx={{ mx: 2, my: 1 }}>
      <AlertTitle>Fechas de planes no consecutivas</AlertTitle>
      <Typography sx={{ mb: 1 }} variant="body2">
        Los registros se pueden guardar, pero la declaración anual no podrá enviarse a revisión
        hasta que estas fechas sean consecutivas.
      </Typography>
      {pairs.map(pair => (
        <Typography key={`${pair.closedPlanId}-${pair.openPlanId}`} variant="body2">
          {describePair(pair, paddockNameById)}
        </Typography>
      ))}
    </Alert>
  )
}

export default NonConsecutiveGrazingPlansWarning
